import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { BuyButton } from 'productTemplate/components/buyBar/BuyButton';
import { convertSearchFilterSizeForBuyBar } from 'productTemplate/utils/convertSearchFilterSizeForBuyBar';
import { getActiveVariantData } from 'productTemplate/utils/getActiveVariantData';
import {
  getBuyNewButtonVariant,
  getBuyUsedButtonVariant,
} from 'productTemplate/utils/getBuyButtonPrice';
import {
  getOffersData,
  getMakeOfferButtonVariant,
} from 'productTemplate/utils/getMakeOfferButtonPrice';
import { getSizeValueFromPresentation } from 'productTemplate/utils/getSizeValueFromPresentation';
import { IMobileBuyBarView } from 'store/types/buyBarTypes';
import {
  setBuyNewAndBuyUsedVariants,
  setMakeOfferVariant,
  setOffersMap,
  setMobileBuyBarView,
} from 'store/slice/buyBarSlice';
import { sendNewMobilePdpBuyBarTrackingEvent } from 'productTemplate/utils/sendNewMobilePdpTrackingEvent';
import { t } from 'localization/utils/t';
import { TABS } from 'productTemplate/enums/buyBarMobile';
import { useFetchBuyBarVariantsQuery } from 'store/api/buyBarVariantsApi';
import { useLocation } from 'shared/hooks/useLocation';
import { useOffersContext } from 'productTemplate/hooks/useOffersContext';
import { useSizeConversion } from 'productTemplate/hooks/useSizeConversion';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';

const TP = 'productTemplate.components.buyBar.BigBuyBarDesktopTabs';

export const BuyButtons = () => {
  // User Size preference
  const { convertedUserSize, isLoading: isLoadingUserSizeConversion } = useSizeConversion();
  // Redux Store
  const storeDispatch = useStoreDispatch();
  const { buyBarSizesMap, buyNewVariant, buyUsedVariant, makeOfferVariant, offersMap } =
    useStoreSelector((state) => state.buyBar);
  const { conditionSearchFilter, instantShipSearchFilter, sizeSearchFilter } = useStoreSelector(
    (state) => state.search,
  );
  const {
    id,
    isAvailabilityRestricted,
    localizedSpecialDisplayPriceCents,
    productCategory,
    productType,
    singleGender,
    sizeBrand,
    sizeOptions,
    sizeUnit,
    slug,
    isFashionProduct,
  } = useStoreSelector((state) => state.pdp.productTemplate);
  // If the URL contains 'sizing' query, that will take precedent in determing the size shown on buy new button
  const { query } = useRouter();
  const sizeFromQuery = query?.sizing
    ? getSizeValueFromPresentation(query.sizing, sizeOptions)
    : '';
  // State
  const [convertedSizeSearchFilter, setConvertedSizeSearchFilter] = useState(sizeSearchFilter);
  const [isLoadingSizeSearchFilterConversion, setIsLoadingSizeSearchFilterConversion] =
    useState(false);
  // Offers
  const {
    existingOffers,
    fetchTopOffers,
    isLoading: isFetchingTopOffers,
    topOffers,
    isOfferable,
  } = useOffersContext();
  const { selectedCountryCode } = useLocation();
  // Loading state for fetching buy bar data
  const { isFetching: isFetchingBuyBarData } = useFetchBuyBarVariantsQuery(
    {
      id,
      selectedCountryCode,
    },
    { skip: isAvailabilityRestricted },
  );
  const isPriceLoading =
    isFetchingBuyBarData || isLoadingUserSizeConversion || isLoadingSizeSearchFilterConversion;

  const buyNewPrice = instantShipSearchFilter
    ? buyNewVariant?.instantShipLowestPriceCents?.amount
    : buyNewVariant?.lowestPriceCents?.amount;
  const buyUsedPrice = buyUsedVariant?.lowestPriceCents?.amount;
  const makeOfferPrice = makeOfferVariant?.topOffer?.amount;

  const openBuyBar = (buyBarView: IMobileBuyBarView) => {
    storeDispatch(setMobileBuyBarView(buyBarView));

    const { activeVariantPrice, productVariants, selectedSize, instantShipPrice, activeVariant } =
      getActiveVariantData({
        buyBarView,
        buyNewVariant,
        buyUsedVariant,
        makeOfferVariant,
        isApparelProduct: isFashionProduct,
        isOfferable,
        offersMap,
        size: convertedUserSize?.size,
        buyBarSizesMap,
      });

    sendNewMobilePdpBuyBarTrackingEvent({
      activeVariant,
      activeVariantPrice,
      buyNewPrice: buyNewPrice || null,
      buyUsedPrice: buyUsedPrice || null,
      currencyCode: localizedSpecialDisplayPriceCents?.currency,
      eventName: 'PDP_BUY_BAR_CTA_TAP',
      instantShipPrice,
      isApparelProduct: isFashionProduct,
      isUnderRetail: activeVariantPrice < localizedSpecialDisplayPriceCents?.amount,
      lastSold: productVariants?.[0]?.lastSold,
      makeOfferPrice: makeOfferPrice || null,
      retailPrice: localizedSpecialDisplayPriceCents?.amount,
      section: buyBarView,
      size: selectedSize,
      slug,
      topOffer: makeOfferPrice || null,
    });
  };

  // Set the Buy New and Buy Used variant
  useEffect(() => {
    const newButtonVariant = getBuyNewButtonVariant({
      conditions: conditionSearchFilter,
      isInstantShip: instantShipSearchFilter,
      sizesMap: buyBarSizesMap,
      sizes: {
        sizingQuery: sizeFromQuery || null,
        sizeSearchFilter: convertedSizeSearchFilter,
        userSize: convertedUserSize?.size,
      },
    });

    const usedButtonVariant = getBuyUsedButtonVariant({
      conditions: conditionSearchFilter,
      sizesMap: buyBarSizesMap,
      sizes: {
        sizeSearchFilter: convertedSizeSearchFilter,
        userSize: convertedUserSize?.size,
      },
    });

    storeDispatch(
      setBuyNewAndBuyUsedVariants({
        buyNewVariant: newButtonVariant,
        buyUsedVariant: usedButtonVariant,
      }),
    );
  }, [buyBarSizesMap, sizeFromQuery, convertedUserSize?.size, convertedSizeSearchFilter]);

  // Size conversion for the filtered sizes from search grid
  useEffect(() => {
    const handleSizeSearchFilterConversion = async () => {
      const data = sizeSearchFilter.map((sizeFilter) =>
        convertSearchFilterSizeForBuyBar({
          brandOfProduct: sizeBrand,
          genderOfProduct: singleGender,
          productCategoryOfProduct: productCategory,
          productTypeOfProduct: productType,
          searchFilterSize: sizeFilter,
          sizeOptions,
          unitOfProduct: sizeUnit,
        }),
      );

      try {
        setIsLoadingSizeSearchFilterConversion(true);
        const result = await Promise.all(data);
        setConvertedSizeSearchFilter(result);
      } catch {
        // do not convert the sizes, the API request will report to bugsnag
      } finally {
        setIsLoadingSizeSearchFilterConversion(false);
      }
    };

    if (sizeSearchFilter.length > 0) {
      void handleSizeSearchFilterConversion();
    }
  }, [sizeSearchFilter, singleGender, productCategory, productType]);

  // Fetch Top Offers
  useEffect(() => {
    void fetchTopOffers();
  }, [id]);

  // Store OffersMap in redux
  useEffect(() => {
    storeDispatch(
      setOffersMap(getOffersData({ buyBarSizesMap, sizeOptions, topOffers, existingOffers })),
    );
  }, [topOffers, existingOffers]);

  // Set Make Offer variant for the Make Offer button
  useEffect(() => {
    if (offersMap) {
      const makeOfferButtonVariant = getMakeOfferButtonVariant({
        sizes: {
          sizeSearchFilter: convertedSizeSearchFilter,
          userSize: convertedUserSize?.size,
        },
        offersMap,
      });
      storeDispatch(setMakeOfferVariant(makeOfferButtonVariant));
    }
  }, [convertedUserSize?.size, convertedSizeSearchFilter, offersMap]);

  return (
    <BuyButtons.Wrapper>
      <BuyButton
        data-qa="buy_new_button"
        title={t(`${TP}.buyNew`, 'Buy New')}
        price={buyNewPrice}
        priceLoading={isPriceLoading}
        onClick={() => openBuyBar(TABS.NEW)}
      />
      <BuyButton
        data-qa="buy_used_button"
        title={t(`${TP}.buyUsed`, 'Buy Used')}
        price={buyUsedPrice}
        priceLoading={isPriceLoading}
        onClick={() => openBuyBar(TABS.USED)}
      />
      <BuyButton
        data-qa="offer_button"
        title={t(`${TP}.makeOffer`, 'Make Offer')}
        price={makeOfferPrice}
        priceLoading={isFetchingTopOffers}
        onClick={() => openBuyBar(TABS.OFFER)}
      />
    </BuyButtons.Wrapper>
  );
};

BuyButtons.Wrapper = styled.div`
  display: flex;
  padding: 0 16px 24px;
  width: 100%;
  justify-content: space-between;
  gap: 8px;
`;
